import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router-dom'
import dayjs from 'dayjs'

import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import StarRating from '@/components/ui/star-rating'
import useDebounce from '@/hooks/useDebounce'
import { useMyReceivedRatings } from '@/modules/InternalRating/hooks/use-internal-rating'
import {
    InternalRating,
    ReceivedRatingFilters,
} from '@/modules/InternalRating/types'

import RatingSummaryCard from './RatingSummaryCard'
import ReceivedFilters from './ReceivedFilters'

const PAGE_SIZE = 20

// Chỉ ticket có trang chi tiết để mở; process/negotiation chưa có route riêng nên
// render text thường thay vì link chết.
const ticketPathOf = (r: InternalRating) =>
    r.entity_type === 'ticket' && r.entity_id > 0
        ? `/tickets/${r.entity_id}`
        : null

// Tab "Điểm của tôi": đánh giá tôi nhận được, kèm TÊN người chấm + comment (H.4).
// W-UI (30/07): dải tổng quan + mỗi card nói rõ VIỆC GÌ được chấm và mở được ticket.
// W-UI2 (30/07): filter (người chấm / khoảng ngày / mức sao / có nhận xét) + tên những
// người cùng được chấm trong một lượt.
const ReceivedList = () => {
    const { t } = useTranslation('rating')
    const [page, setPage] = useState(1)

    const [star, setStar] = useState<number | undefined>()
    const [fromDate, setFromDate] = useState('')
    const [toDate, setToDate] = useState('')
    const [raterInput, setRaterInput] = useState('')
    const [withComment, setWithComment] = useState<boolean | undefined>()

    // Debounce ô tìm kiếm: gõ 1 ký tự không được thành 1 request. ILIKE '%x%' không
    // dùng được index nên mỗi request là một lần quét — đáng để chờ 400ms.
    const raterSearch = useDebounce(raterInput, 400)

    const filters = useMemo<ReceivedRatingFilters>(
        () => ({
            star,
            from_date: fromDate || undefined,
            to_date: toDate || undefined,
            rater_search: raterSearch || undefined,
            with_comment: withComment,
        }),
        [star, fromDate, toDate, raterSearch, withComment]
    )

    // hasActive phải suy từ giá trị ĐÃ debounce, không phải raw input. Nếu dùng raw:
    // bấm "Xoá lọc" -> raterInput rỗng ngay -> hasActive=false ngay, nhưng query vẫn
    // đang giữ kết quả đã lọc trong 400ms -> hiện "Bạn chưa nhận được đánh giá nào"
    // cho người có 50 lượt.
    const hasActive =
        star !== undefined ||
        !!fromDate ||
        !!toDate ||
        !!raterSearch ||
        withComment !== undefined

    const { data, renderState, isFetching } = useMyReceivedRatings(
        page,
        PAGE_SIZE,
        filters
    )

    // Đổi filter phải về trang 1 (đang ở trang 5 rồi lọc còn 1 trang thì sẽ trắng),
    // và reset NGAY TRONG HANDLER, không dùng useEffect.
    //
    // Vì sao: useBaseQuery áp option mới trong một React.useEffect
    // (node_modules/@tanstack/react-query/.../useBaseQuery.js:67-68), và
    // queryObserver.shouldFetchOptionally (queryObserver.js:449-451) thấy queryKey đổi
    // thì gọi #executeFetch. Nếu reset page bằng useEffect thì trong CÙNG commit đó
    // effect của query vẫn chạy với page CŨ + filter MỚI -> fetch page=5, rồi setPage(1)
    // mới gây re-render -> fetch page=1. Hai request, cái đầu bị bỏ đi ngay.
    // (Mỗi request my-received còn kéo theo aggregate GetMyRatingSummary.)
    //
    // Với các filter tức thời (sao / ngày / có nhận xét) cách này cho đúng 1 request.
    // Riêng ô tìm kiếm vẫn là 2 (page=1+từ khoá cũ, rồi page=1+từ khoá mới sau 400ms) —
    // không tránh được khi có debounce, nhưng request đầu trả dữ liệu người dùng THẤY,
    // khác với bản cũ trả trang 5 rồi bỏ.
    const onPage1 =
        <T,>(set: (v: T) => void) =>
        (v: T) => {
            setPage(1)
            set(v)
        }

    const reset = () => {
        setPage(1)
        setStar(undefined)
        setFromDate('')
        setToDate('')
        setRaterInput('')
        setWithComment(undefined)
    }

    // KHÔNG early-return trên state: nếu return sớm thì lỗi API sẽ xoá luôn cả filter
    // bar, người dùng không còn cách nào bỏ bộ lọc đang gây lỗi — nút duy nhất còn lại
    // là "Thử lại" cùng một query. Render filter trước, state vào chỗ danh sách.
    const state = renderState()
    const items = data?.items ?? []

    return (
        <div className="space-y-3">
            {/* Tổng quan cố ý KHÔNG theo filter: nó là "điểm của tôi", không phải
                "điểm của kết quả đang lọc". Bấm vào thanh phân bố = lọc theo mức sao. */}
            <RatingSummaryCard
                summary={data?.summary}
                activeStar={star}
                onStarSelect={onPage1((s: number) =>
                    setStar(star === s ? undefined : s)
                )}
            />

            <ReceivedFilters
                star={star}
                onStarChange={onPage1(setStar)}
                fromDate={fromDate}
                onFromDateChange={onPage1(setFromDate)}
                toDate={toDate}
                onToDateChange={onPage1(setToDate)}
                raterInput={raterInput}
                onRaterInputChange={onPage1(setRaterInput)}
                withComment={withComment}
                onWithCommentChange={onPage1(setWithComment)}
                onReset={reset}
                hasActive={hasActive}
            />

            {state ? (
                state
            ) : !items.length ? (
                <p className="py-10 text-center text-sm text-gray-400">
                    {hasActive
                        ? t('page.empty_filtered')
                        : t('page.empty_received')}
                </p>
            ) : (
                items.map((r) => {
                    const path = ticketPathOf(r)
                    // entity_title rỗng khi BE chưa deploy hoặc ticket đã xoá -> vẫn
                    // còn mã để bấm vào, chỉ là không có tiêu đề.
                    const label = r.entity_title
                        ? `#${r.entity_id} · ${r.entity_title}`
                        : `#${r.entity_id}`
                    // Chỉ hiện khi có NGƯỜI KHÁC cùng được chấm; một mình mình thì
                    // dòng này không mang thông tin gì.
                    const co = r.rated_employee_names ?? []

                    return (
                        <div
                            key={r.rating_id}
                            className="rounded-lg border border-gray-100 bg-gray-50/50 p-3"
                        >
                            <div className="flex items-center justify-between gap-2">
                                <div className="flex items-center gap-2">
                                    <StarRating
                                        value={r.rating}
                                        readOnly
                                        size="sm"
                                        ariaLabel={`${r.rating}/5`}
                                    />
                                    <span className="text-sm font-medium text-gray-700">
                                        {r.rating.toFixed(1)}
                                    </span>
                                </div>
                                <span className="shrink-0 text-xs text-gray-400">
                                    {dayjs(r.created_at).format(
                                        'DD/MM/YYYY HH:mm'
                                    )}
                                </span>
                            </div>

                            <div className="mt-1.5 flex flex-wrap items-center gap-x-2 gap-y-1">
                                {path ? (
                                    <Link
                                        to={path}
                                        className="truncate text-sm font-medium text-primary hover:underline"
                                        title={t('page.view_ticket')}
                                    >
                                        {label} →
                                    </Link>
                                ) : (
                                    <span className="truncate text-sm font-medium text-gray-700">
                                        {label}
                                    </span>
                                )}
                                {r.entity_type_label && (
                                    <Badge variant="secondary">
                                        {r.entity_type_label}
                                    </Badge>
                                )}
                            </div>

                            <div className="mt-1 text-sm text-gray-600">
                                {t('page.rated_by')}:{' '}
                                <span className="font-medium text-gray-800">
                                    {r.rater_employee_name ||
                                        `#${r.rater_employee_id}`}
                                </span>
                                {r.rater_employee_code
                                    ? ` (${r.rater_employee_code})`
                                    : ''}
                                {r.rater_department_name
                                    ? ` · ${r.rater_department_name}`
                                    : ''}
                            </div>

                            {co.length > 1 && (
                                <div className="mt-0.5 text-xs text-gray-500">
                                    {t('page.rated_together')}: {co.join(', ')}
                                </div>
                            )}

                            {r.comment && (
                                <p className="mt-1 text-sm italic text-gray-500">
                                    “{r.comment}”
                                </p>
                            )}
                        </div>
                    )
                })
            )}

            {!state && (
                <div className="flex items-center justify-center gap-2 pt-1">
                    <Button
                        variant="outline"
                        size="sm"
                        disabled={page <= 1 || isFetching}
                        onClick={() => setPage((p) => Math.max(1, p - 1))}
                    >
                        {t('page.prev')}
                    </Button>
                    <span className="text-xs text-gray-400">{page}</span>
                    <Button
                        variant="outline"
                        size="sm"
                        disabled={!data?.has_more || isFetching}
                        onClick={() => setPage((p) => p + 1)}
                    >
                        {t('page.next')}
                    </Button>
                </div>
            )}
        </div>
    )
}

export default ReceivedList
